import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { loadable } from "jotai/utils";
import { useRef } from "react";

import { Logger } from "@moment/logging";

import { $isDocumentPublished } from "../../internal/document/sharing";
import { $resetShareTabState } from "../../internal/document/sharing/pending-permissions.atom";
import { type PermissionUpdate } from "../../internal/document/sharing/permissions.types";
import {
	$loadableSyncCurrentSnapshot,
	$plannedOutcomePublish,
	$plannedOutcomeShareSync,
	type PlannedOutcomeResult,
	calculatePlanStatus,
} from "../../internal/document/sync/current-state.workflow.atom";
import { $executeCommitWorkflow } from "../../internal/document/sync/local-commit.workflow.atom";
import {
	$publishPlanEffect,
	$sharePlanEffect,
} from "../../internal/document/sync/publish-plan.effect";
import {
	$executePublishAndPushWorkflow,
	$executeUnpublishWorkflow,
} from "../../internal/document/sync/publish.workflow.atom";
import { $executeShareCollabWorkflow } from "../../internal/document/sync/share-collab.workflow.atom";
import {
	$isSyncing,
	$publishPlan,
	$sharePlan,
	$skipCollab,
	$syncAction,
	$syncError,
	$syncState,
} from "../../internal/document/sync/sync-state.atom";
import type {
	PlanStatus,
	PlannedOutcome,
	SyncAction,
	SyncSnapshot,
} from "../../internal/document/sync/sync.types";
import type { Workspace } from "../../internal/workspaces";
import { useDocumentHasLocal } from "./useDocumentHasLocal";

export type { PlannedOutcomeResult };

const logger = new Logger("useSyncSnapshot");

export const useSyncSnapshot = (documentId: string | undefined) => {
	const [snapshot] = useAtom($loadableSyncCurrentSnapshot(documentId));
	return {
		isLoading: snapshot.state === "loading",
		snapshot: snapshot.state === "hasData" ? snapshot.data : undefined,
	};
};

export const usePlannedOutcomePublish = (
	documentId: string | undefined
): PlannedOutcomeResult & { isLoading: boolean } => {
	const loadablePlannedOutcome = loadable($plannedOutcomePublish(documentId));
	const plannedOutcome = useAtomValue(loadablePlannedOutcome);
	if (plannedOutcome.state === "hasError") {
		logger.error("Error fetching planned outcome for publish", {
			error: plannedOutcome.error,
		});
		return {
			isLoading: false,
			canExecute: false,
			plannedOutcome: null,
			planStatus: "unachievable-unknown-error" as PlanStatus,
		};
	}

	return {
		isLoading: plannedOutcome.state === "loading",
		canExecute: plannedOutcome.state === "hasData" ? plannedOutcome.data.canExecute : false,
		plannedOutcome:
			plannedOutcome.state === "hasData" ? plannedOutcome.data.plannedOutcome : null,
		planStatus:
			plannedOutcome.state === "hasData"
				? plannedOutcome.data.planStatus
				: ("status-pending" as PlanStatus),
	};
};

export const usePlannedOutcomeShareSync = (
	documentId: string | undefined
): PlannedOutcomeResult & { isLoading: boolean } => {
	const loadablePlannedOutcome = loadable($plannedOutcomeShareSync(documentId));
	const plannedOutcome = useAtomValue(loadablePlannedOutcome);
	if (plannedOutcome.state === "hasError") {
		logger.error("Error fetching planned outcome for share-sync", {
			error: plannedOutcome.error,
		});
		return {
			isLoading: false,
			canExecute: false,
			plannedOutcome: null,
			planStatus: "unachievable-unknown-error" as PlanStatus,
		};
	}

	return {
		isLoading: plannedOutcome.state === "loading",
		canExecute: plannedOutcome.state === "hasData" ? plannedOutcome.data.canExecute : false,
		plannedOutcome:
			plannedOutcome.state === "hasData" ? plannedOutcome.data.plannedOutcome : null,
		planStatus:
			plannedOutcome.state === "hasData"
				? plannedOutcome.data.planStatus
				: ("status-pending" as PlanStatus),
	};
};

export const useShareCollabWorkflow = (documentId: string | undefined) => {
	const executeWorkflow = useSetAtom($executeShareCollabWorkflow);
	const isSyncing = useAtomValue($isSyncing(documentId));
	const syncAction = useAtomValue($syncAction(documentId));
	const syncError = useAtomValue($syncError(documentId));
	const setError = useSetAtom($syncError(documentId));

	const execute = async (input: {
		snap: SyncSnapshot;
		plannedOutcome: PlannedOutcomeResult;
		pendingPermissions: Map<string, PermissionUpdate>;
		workspace?: Workspace;
		skipCollab?: boolean;
	}) => {
		await executeWorkflow(input);
	};

	const resetError = () => {
		setError(null);
	};

	return {
		execute,
		isSyncing,
		syncAction,
		syncError,
		resetError,
	};
};

export const useSyncState = (documentId: string | undefined) => {
	const syncAction = useAtomValue($syncAction(documentId));
	const syncError = useAtomValue($syncError(documentId));
	const syncState = useAtomValue($syncState(documentId));
	const setError = useSetAtom($syncError(documentId));

	const resetError = () => {
		setError(null);
	};

	return {
		isSyncing: syncState.isSyncing,
		currentWorkflow: syncState.currentWorkflow,
		syncAction,
		syncError,
		resetError,
	};
};

export const usePublishWorkflow = (documentId: string | undefined) => {
	const executeWorkflow = useSetAtom($executePublishAndPushWorkflow);
	const executeUnpublish = useSetAtom($executeUnpublishWorkflow);
	const setSyncError = useSetAtom($syncError(documentId));
	const syncState = useAtomValue($syncState(documentId));
	const syncAction = useAtomValue($syncAction(documentId));
	const syncError = useAtomValue($syncError(documentId));
	const loadableIsPublished = useAtomValue(loadable($isDocumentPublished(documentId)));
	const { hasLocalDocument, isLoading: isCheckingLocalDocument } =
		useDocumentHasLocal(documentId);

	const execute = async (input: {
		snap: SyncSnapshot;
		plannedOutcome: PlannedOutcomeResult;
		pendingPermissions?: Map<string, PermissionUpdate>;
		workspace?: Workspace;
		commitMessage: string;
		password?: string;
	}) => {
		if (isCheckingLocalDocument) {
			const error = new Error(
				"Checking local document status. Please try again in a moment."
			);
			setSyncError(error);
			throw error;
		}

		if (!hasLocalDocument) {
			const error = new Error(
				"Publishing requires a local document. Clone the document locally before publishing."
			);
			setSyncError(error);
			throw error;
		}

		await executeWorkflow(input);
	};

	const unpublish = async (input: { snap: SyncSnapshot }) => {
		if (isCheckingLocalDocument) {
			const error = new Error(
				"Checking local document status. Please try again in a moment."
			);
			setSyncError(error);
			throw error;
		}

		if (!hasLocalDocument) {
			const error = new Error(
				"Unpublishing requires a local document. Clone the document locally before unpublishing."
			);
			setSyncError(error);
			throw error;
		}

		await executeUnpublish(input);
	};

	return {
		execute,
		unpublish,
		canUnpublish:
			!isCheckingLocalDocument &&
			hasLocalDocument &&
			(loadableIsPublished.state === "hasData" ? loadableIsPublished.data : false),
		isCheckingLocalDocument,
		isSyncing: syncState.isSyncing,
		syncAction,
		syncError,
	};
};

export const useLocalCommitWorkflow = (documentId: string | undefined) => {
	const executeWorkflow = useSetAtom($executeCommitWorkflow);
	const syncState = useAtomValue($syncState(documentId));
	const syncAction = useAtomValue($syncAction(documentId));
	const syncError = useAtomValue($syncError(documentId));

	return {
		execute: async (input: { snap: SyncSnapshot; commitMessage?: string }) => {
			await executeWorkflow(input);
		},
		isSyncing: syncState.isSyncing,
		syncAction,
		syncError,
	};
};

// this is the logic of what the plan outcome should be if the user is not planning on sharing.
// Aka anything not in the share modal.
export const usePlannedOutcomeSync = (
	documentId: string | undefined
): PlannedOutcomeResult & { isLoading: boolean } => {
	const {
		isLoading: isShareSyncLoading,
		canExecute,
		plannedOutcome: plannedOutcomeShareSync,
	} = usePlannedOutcomeShareSync(documentId);
	const {
		isLoading: isPublishLoading,
		canExecute: canPublishExecute,
		plannedOutcome: plannedOutcomePublish,
	} = usePlannedOutcomePublish(documentId);
	const { snapshot, isLoading: isSnapshotLoading } = useSyncSnapshot(documentId);

	if (isSnapshotLoading || !snapshot) {
		return {
			isLoading: true,
			canExecute: false,
			plannedOutcome: null,
			planStatus: "status-pending" as PlanStatus,
		};
	}

	if (!snapshot.remoteRepoExists) {
		return {
			isLoading: isSnapshotLoading,
			plannedOutcome: snapshot.repoClean ? null : ("commit" as PlannedOutcome),
			canExecute: snapshot.repoClean ? false : true,
			planStatus: snapshot.repoClean
				? ("unachievable-nothing-to-do" as PlanStatus)
				: ("achievable" as PlanStatus),
		};
	}

	// TODO this needs to change to check if the document is published.
	// for but now, use the collab tracking branch as a proxy.
	if (!snapshot.collabTrackingBranchSet) {
		// published document
		return {
			isLoading: isPublishLoading,
			canExecute: canPublishExecute,
			plannedOutcome: plannedOutcomePublish,
			planStatus: calculatePlanStatus(snapshot, plannedOutcomePublish) as PlanStatus,
		};
	}

	// else return shared sync outcome
	return {
		isLoading: isShareSyncLoading,
		canExecute: canExecute,
		plannedOutcome: plannedOutcomeShareSync,
		planStatus: calculatePlanStatus(snapshot, plannedOutcomeShareSync) as PlanStatus,
	};
};

export const usePublishPlan = (documentId: string | undefined) => {
	// Subscribe to the effect - this activates automatic plan generation
	useAtomValue($publishPlanEffect(documentId));

	const plan = useAtomValue($publishPlan(documentId));
	const currentAction = useAtomValue($syncAction(documentId));
	const { isLoading: isSnapshotLoading } = useSyncSnapshot(documentId);
	const { canExecute, isLoading: isOutcomeLoading } = usePlannedOutcomePublish(documentId);

	const isLoading = isSnapshotLoading || isOutcomeLoading;

	// Track completed actions in refs, updated during render (not in useEffect)
	const completedRef = useRef<Set<SyncAction>>(new Set());
	const prevActionRef = useRef<SyncAction | undefined>();
	const prevPlanRef = useRef<SyncAction[]>([]);
	const isSyncing = useAtomValue($isSyncing(documentId));
	const wasSyncingRef = useRef(false);

	// Track if we were syncing
	if (isSyncing) {
		wasSyncingRef.current = true;
	}

	// Reset completed when plan changes (but not after sync completes)
	if (plan !== prevPlanRef.current) {
		if (!wasSyncingRef.current) {
			completedRef.current = new Set();
		}
		wasSyncingRef.current = false;
		prevPlanRef.current = plan;
	}

	// Track completed actions during render
	if (prevActionRef.current && prevActionRef.current !== currentAction) {
		completedRef.current = new Set([...completedRef.current, prevActionRef.current]);
	}
	prevActionRef.current = currentAction;

	const completed = completedRef.current;

	return {
		plan,
		currentAction,
		completed,
		isLoading,
		canExecute,
	};
};

export const useSharePlan = (documentId: string | undefined) => {
	// Subscribe to the effect - this activates automatic plan generation
	useAtomValue($sharePlanEffect(documentId));

	const plan = useAtomValue($sharePlan(documentId));
	const currentAction = useAtomValue($syncAction(documentId));
	const { isLoading: isSnapshotLoading } = useSyncSnapshot(documentId);
	const { canExecute, isLoading: isOutcomeLoading } = usePlannedOutcomeShareSync(documentId);

	const isLoading = isSnapshotLoading || isOutcomeLoading;

	// Track completed actions in refs, updated during render (not in useEffect)
	const completedRef = useRef<Set<SyncAction>>(new Set());
	const prevActionRef = useRef<SyncAction | undefined>();
	const prevPlanRef = useRef<SyncAction[]>([]);
	const isSyncing = useAtomValue($isSyncing(documentId));
	const wasSyncingRef = useRef(false);

	// Track if we were syncing
	if (isSyncing) {
		wasSyncingRef.current = true;
	}

	// Reset completed when plan changes (but not after sync completes)
	if (plan !== prevPlanRef.current) {
		if (!wasSyncingRef.current) {
			completedRef.current = new Set();
		}
		wasSyncingRef.current = false;
		prevPlanRef.current = plan;
	}

	// Track completed actions during render
	if (prevActionRef.current && prevActionRef.current !== currentAction) {
		completedRef.current = new Set([...completedRef.current, prevActionRef.current]);
	}
	prevActionRef.current = currentAction;

	const completed = completedRef.current;

	return {
		plan,
		currentAction,
		completed,
		isLoading,
		canExecute,
	};
};

export const useSkipCollab = (documentId: string | undefined) => {
	return useAtom($skipCollab(documentId));
};

export const useResetShareTabState = () => {
	return useSetAtom($resetShareTabState);
};
